import { createPortal } from "react-dom";
import { Loader2 } from "lucide-react";
import { useState } from "react";
import {
  Button,
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  CardFooter,
  Label,
  RadioGroup,
  RadioGroupItem,
} from "@repo/ui";

interface IntroModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  onDecline: () => void;
  isLoading: boolean;
  error?: string | null;
}

export function IntroModal({
  isOpen,
  onConfirm,
  onDecline,
  isLoading,
  error,
}: IntroModalProps) {
  const [q1Response, setQ1Response] = useState<boolean | null>(null);
  const [q2Response, setQ2Response] = useState<boolean | null>(null);

  // Peut commencer seulement si les 2 réponses sont OUI
  const canStart = q1Response === true && q2Response === true;
  // A refusé si une réponse est NON
  const hasDeclined = q1Response === false || q2Response === false;

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <Card className="w-full max-w-2xl mx-4 max-h-[90vh] flex flex-col overflow-hidden relative">
        {isLoading && (
          <div className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-4 bg-background/90 backdrop-blur-sm rounded-surface">
            <Loader2 className="h-10 w-10 animate-spin text-primary" />
            <p className="text-base font-medium text-foreground">
              Initialisation de l'entretien en cours...
            </p>
          </div>
        )}
        <CardHeader className="text-center flex-shrink-0">
          {/* Logo Aix-Marseille Université */}
          {/* <div className="mx-auto mb-4">
            <img
              src="/logos/aix-marseille-universite-logo.jpg"
              alt="Aix-Marseille Université"
              className="w-24 h-24 object-contain mx-auto"
            />
          </div> */}
          <CardTitle className="text-2xl">Notice d'information</CardTitle>
          <CardDescription className="text-base mt-2">
            FairView
          </CardDescription>
        </CardHeader>
        {/* `pb-1` : les radios de la dernière question touchent le bas de la
            zone défilante, qui rognait leur anneau de focus même défilée au
            maximum (`scroll-margin` ne peut pas défiler au-delà du contenu). */}
        <CardContent className="text-sm text-muted-foreground space-y-4 overflow-y-auto flex-1 pb-1">
          {/* Introduction */}
          <p className="font-medium">Madame, Mademoiselle, Monsieur,</p>

          <p>
            Vous allez passer un entretien dans le cadre du processus de
            recrutement pour le poste auquel vous avez candidaté. Il s'agit d'un
            <span className="font-bold"> entretien structuré</span>, dans lequel
            les questions :
          </p>

          <ul className="list-disc list-inside space-y-1 ml-4">
            <li>évaluent uniquement des compétences requises pour le poste</li>
            <li>sont identiques pour tous les candidats</li>
          </ul>

          <p>
            La version automatisée de cet entretien, que vous allez passer, fait
            l'objet d'une recherche réalisée par le Laboratoire Lorrain de
            Psychologie et Neurosciences (2LPN) et supervisée par Vincent
            Berthet, maître de conférences à l'université de Lorraine. Pour
            toute demande d'informations, vous pouvez envoyer un email à :{" "}
            <a
              href="mailto:vincent.berthet@univ-lorraine.fr"
              className="text-primary underline"
            >
              vincent.berthet@univ-lorraine.fr
            </a>
          </p>

          {/* Objectif de la recherche */}
          <div className="mt-6">
            <h3 className="font-semibold text-foreground">
              Objectif de la recherche
            </h3>
            <p className="mt-2">
              Cette recherche vise à tester une version automatisée de
              l'entretien structuré.
            </p>
          </div>

          {/* Déroulement */}
          <div className="mt-6">
            <h3 className="font-semibold text-foreground">
              Comment se déroule l'entretien ?
            </h3>
            <ul className="mt-2 list-disc list-inside space-y-1 ml-4">
              <li>
                Il vous suffit de répondre aux questions qui vous seront posées.
                Pour cela, mettez-vous dans un endroit au calme.
              </li>
              <li>
                Vous pouvez répondre aux questions soit à l'oral, soit par
                écrit. Nous vous encourageons à répondre à l'oral.
              </li>
              <li>
                Relisez chacune de vos réponses avant de la valider, vous
                pouvez la modifier par écrit si besoin.
              </li>
              <li className="font-bold">
                Nous vous décourageons fortement de recourir à une IA : cet
                usage pourra être détecté et susceptible de vous pénaliser.
              </li>
              <li>L'entretien dure 20 min environ.</li>
            </ul>
          </div>

          {/* Remarques */}
          <div className="mt-6">
            <h3 className="font-semibold text-foreground">Remarques :</h3>
            <ul className="mt-2 list-disc list-inside space-y-1 ml-4">
              <li>
                Les données recueillies lors de cette recherche seront
                exploitées uniquement dans le cadre de ce recrutement et à des
                fins de recherche de façon strictement confidentielle.
              </li>
              <li>
                Cet entretien s'inscrit dans un processus de recrutement dont
                la supervision est totalement humaine. Les résultats à cet
                entretien constituent un élément parmi d'autres que le
                recruteur pourra prendre en compte. La décision de recrutement
                est prise par le recruteur, en aucun cas par l'IA.
              </li>
            </ul>
            <p className="mt-2">
              Un recruteur reviendra vers vous pour la suite du processus.
            </p>
          </div>

          {/* Droits */}
          <div className="mt-6">
            <h3 className="font-semibold text-foreground">
              Quels sont vos droits en tant que participant(e) à cette recherche
              ?
            </h3>
            <p className="mt-2">
              Vous pouvez refuser de participer à cette recherche sans avoir à
              vous justifier.
            </p>
          </div>

          {/* Questions de consentement */}
          <div className="mt-8 pt-6 border-t space-y-6">
            <h3 className="font-semibold text-foreground text-center">
              Consentement
            </h3>

            {/* Question 1 */}
            <div className="space-y-3">
              <p className="font-medium text-foreground">
                J'ai pris connaissance des informations m'expliquant le
                protocole de recherche mentionné ci-dessus :
              </p>
              <RadioGroup
                value={q1Response === null ? "" : q1Response ? "oui" : "non"}
                onValueChange={(value) => setQ1Response(value === "oui")}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="oui" id="q1-oui" />
                  <Label htmlFor="q1-oui" className="cursor-pointer">
                    OUI
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="non" id="q1-non" />
                  <Label htmlFor="q1-non" className="cursor-pointer">
                    NON
                  </Label>
                </div>
              </RadioGroup>
            </div>

            {/* Question 2 */}
            <div className="space-y-3">
              <p className="font-medium text-foreground">
                Je consens à passer cet entretien :
              </p>
              <RadioGroup
                value={q2Response === null ? "" : q2Response ? "oui" : "non"}
                onValueChange={(value) => setQ2Response(value === "oui")}
                className="flex gap-6"
              >
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="oui" id="q2-oui" />
                  <Label htmlFor="q2-oui" className="cursor-pointer">
                    OUI
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <RadioGroupItem value="non" id="q2-non" />
                  <Label htmlFor="q2-non" className="cursor-pointer">
                    NON
                  </Label>
                </div>
              </RadioGroup>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex flex-col gap-3 shrink-0">
          {error && (
            <p className="w-full text-sm text-destructive text-center">
              {error}
            </p>
          )}
          <div className="flex flex-col-reverse sm:flex-row gap-3 sm:gap-4 sm:justify-end w-full">
            <Button
              variant="outline"
              onClick={onDecline}
              disabled={isLoading}
              className="min-w-32"
            >
              Refuser
            </Button>
            <Button
              onClick={onConfirm}
              disabled={!canStart || isLoading}
              className="min-w-40"
              size="lg"
            >
              Commencer l'entretien
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>,
    document.body,
  );
}
